import { useQuery } from '@tanstack/react-query';
import type { Database } from '@pirouette/db';
import { supabase } from '@/lib/supabase';

// Recital Operations data layer.
//
// Mirrors @/data/bi.ts and @/data/costumes.ts: thin, typed react-query wrappers
// over `supabase.rpc(...)`. Row types are derived from the generated
// `@pirouette/db` Database types so the UI tracks the database contract.
// Nothing here bypasses RLS — each call runs as the authenticated admin and the
// per-table policies scope it to the caller's studio (tenant).

type RpcReturns<K extends keyof Database['public']['Functions']> =
  Database['public']['Functions'][K]['Returns'];

// ---------------------------------------------------------------------------
// Recital dashboard — recital_dashboard(p_production_id) returns a single
// `jsonb` object, so the generated type is `Json`. We model the contract
// explicitly here and narrow the payload at the read boundary (same approach
// bi.ts uses for the `Json`-typed bi_owner_snapshot RPC). Treat every field as
// possibly absent — it is computed server-side from whatever data exists.
// ---------------------------------------------------------------------------

/** Costume readiness rollup embedded in the recital dashboard payload. */
export interface RecitalCostumeReadiness {
  total: number;
  measured: number;
  sized: number;
  ordered: number;
  received: number;
  distributed: number;
  exceptions: number;
  cost_cents: number;
  paid_cents: number;
}

export interface RecitalDashboard {
  routines: number;
  dancers: number;
  shows: number;
  quick_change_conflicts: number;
  costume: RecitalCostumeReadiness;
}

/**
 * One quick-change conflict row: a dancer who appears in two routines within
 * `p_max_gap` slots of each other. `severity` is the RPC's own grading.
 */
export type QuickChangeConflictRow = RpcReturns<'quick_change_conflicts'>[number];

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Recital readiness rollup for one production (routines, dancers, shows, costume). */
export function useRecitalDashboard(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['recital', 'dashboard', productionId],
    queryFn: async (): Promise<RecitalDashboard> => {
      const { data, error } = await supabase.rpc('recital_dashboard', {
        p_production_id: productionId!,
      });
      if (error) throw error;
      return narrowDashboard(data);
    },
  });
}

/** Per-show quick-change conflicts (dancer appears again within `maxGap` slots). */
export function useQuickChangeConflicts(showId: string | undefined, maxGap = 2) {
  return useQuery({
    enabled: !!showId,
    queryKey: ['recital', 'quick-change', showId, maxGap],
    queryFn: async (): Promise<QuickChangeConflictRow[]> => {
      const { data, error } = await supabase.rpc('quick_change_conflicts', {
        p_show_id: showId!,
        p_max_gap: maxGap,
      });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Narrow the `costume` block of the dashboard payload into typed counts. */
function narrowCostume(value: unknown): RecitalCostumeReadiness {
  const obj = (value ?? {}) as Record<string, unknown>;
  return {
    total: asNumber(obj.total),
    measured: asNumber(obj.measured),
    sized: asNumber(obj.sized),
    ordered: asNumber(obj.ordered),
    received: asNumber(obj.received),
    distributed: asNumber(obj.distributed),
    exceptions: asNumber(obj.exceptions),
    cost_cents: asNumber(obj.cost_cents),
    paid_cents: asNumber(obj.paid_cents),
  };
}

/** Narrow the `Json` recital_dashboard payload into a typed {@link RecitalDashboard}. */
function narrowDashboard(value: unknown): RecitalDashboard {
  const obj = (value ?? {}) as Record<string, unknown>;
  return {
    routines: asNumber(obj.routines),
    dancers: asNumber(obj.dancers),
    shows: asNumber(obj.shows),
    quick_change_conflicts: asNumber(obj.quick_change_conflicts),
    costume: narrowCostume(obj.costume),
  };
}
